import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildNodeIconPath } from '@/lib/imagePaths'
import type { RecoveryObject } from '../../api'

export type ChangeState = 'flash' | 'highlight' | null

/** A single recovery object: icon, id, description, engineer, runbook progress. */
export function RecoveryObjectCard({ obj, changeState }: { obj: RecoveryObject; changeState: ChangeState }) {
    const { t, i18n } = useTranslation()
    const [imgErr, setImgErr] = useState(false)
    const nf = new Intl.NumberFormat(i18n.language || undefined)

    const total = obj.tasksTotal || 0
    const completed = obj.tasksCompleted || 0
    const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0
    const statusLc = (obj.status || '').toLowerCase()
    const isIssue = statusLc.includes('issue') || statusLc.includes('block') || statusLc.includes('fail')
    const iconUrl = !imgErr ? buildNodeIconPath(obj.icon) : null

    return (
        <div
            className={cn(
                'rounded-lg border bg-card p-3 transition-colors',
                changeState === 'highlight' && 'border-warning ring-1 ring-warning/40',
                changeState === 'flash' && 'animate-pulse border-warning ring-2 ring-warning/60',
            )}
        >
            <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30">
                    {iconUrl ? (
                        <img src={iconUrl} alt="" className="h-7 w-7 object-contain" onError={() => setImgErr(true)} />
                    ) : (
                        <Box className="h-5 w-5 text-muted-foreground" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">{obj.id}</div>
                    <div className="truncate text-xs text-muted-foreground" title={obj.description}>
                        {obj.description}
                    </div>
                </div>
            </div>

            <div className="mt-2 truncate text-xs">
                {obj.engineer ? (
                    <span className="text-foreground">
                        <span className="text-muted-foreground">{t('recoverRequests.locationTracking.engineer', 'Engineer:')}</span> {obj.engineer}
                    </span>
                ) : (
                    <span className="italic text-muted-foreground">{t('recoverRequests.locationTracking.unassigned', 'Unassigned')}</span>
                )}
            </div>

            <div className="mt-2">
                <div className="mb-0.5 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="tabular-nums">
                        {nf.format(completed)}/{nf.format(total)}
                    </span>
                    <span className="tabular-nums">{pct}%</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                        className={cn('h-full rounded-full transition-all', isIssue ? 'bg-destructive' : 'bg-success')}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>
        </div>
    )
}
